import {
  DeviceHomeAlertData,
  HomeSections,
  SoftwareHomeAlertData,
} from '@order-management/types';
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Columns, Element } from 'react-bulma-components';
import { CgSpinner } from 'react-icons/cg';
import { useNavigate } from 'react-router-dom';
import { fetchDevicesHomeAlertData } from '../../devices/devices-service';
import { fetchSoftwareSeatsHomeAlertData } from '../../software/software-service';
import StepTab from '../../ui/step-tab/step-tab';
import Conditional from '../conditional/conditional';
import {
  HomeSectionsContext,
  HomeSectionsRefreshAction,
} from '../context/home-sections-context';
import useHttpRequest from '../hooks/http-request';
import useIsCustomerUser from '../hooks/is-customer-user';
import useIsZspaceInternalUser from '../hooks/is-zspace-internal-user';
import useUserPendingTasks from '../hooks/user-pending-tasks';
import TaskTag from '../task-tag/task-tag';
import styles from './home-sections-header-tabs.module.scss';

export type HomeSectionsHeaderTabsProps = {
  activeTab: HomeSections;
};

type InformationTag = { tasks: string[]; isLoading: boolean };

type StepData = {
  title: HomeSections;
  informationTag?: ReactNode;
  onClick: () => void;
};

export function HomeSectionsHeaderTabs({
  activeTab,
}: HomeSectionsHeaderTabsProps) {
  const navigate = useNavigate();
  const isCustomerUser = useIsCustomerUser();
  const isZspaceInternalUser = useIsZspaceInternalUser();
  const [devicesTasksData, setDevicesTasksData] = useState<DeviceHomeAlertData>(
    {
      ungroupedDevicesCount: 0,
      unregisteredDevicesCount: 0,
    }
  );
  const [softwareTasksData, setSoftwareTasksData] =
    useState<SoftwareHomeAlertData>({ unassignedSoftwareCount: 0 });
  const userTasks = useUserPendingTasks(devicesTasksData, softwareTasksData);
  const { refreshAction } = useContext(HomeSectionsContext);
  const {
    executeHttpRequest: executeFetchDevicesHomeAlertRequest,
    isLoading: isLoadingDevicesHomeAlert,
  } = useHttpRequest();
  const {
    executeHttpRequest: executeFetchSoftwareSeatsHomeAlertRequest,
    isLoading: isLoadingSoftwareSeatsHomeAlert,
  } = useHttpRequest();

  const isZspaceInternalOrCustomerUser = useMemo(
    () => isZspaceInternalUser || isCustomerUser,
    [isCustomerUser, isZspaceInternalUser]
  );

  const navigateToMySalesOrdersPage = useCallback(() => {
    navigate('/my-sales-orders');
  }, [navigate]);

  const navigateToMyTeamPage = useCallback(() => {
    navigate('/users/my-team');
  }, [navigate]);

  const navigateToDevicesPage = useCallback(() => {
    navigate('/my-devices');
  }, [navigate]);

  const navigateToSoftwarePage = useCallback(() => {
    navigate('/my-software');
  }, [navigate]);

  const InformationTag = useCallback(({ tasks, isLoading }: InformationTag) => {
    if (isLoading) {
      return <CgSpinner className="animate-spin" />;
    }
    return (
      <Conditional condition={tasks.length > 0}>
        <Conditional.True>
          {tasks.map((task) => {
            return <TaskTag taskContent={task} key={task} pending />;
          })}
        </Conditional.True>
        <Conditional.False>
          <TaskTag />
        </Conditional.False>
      </Conditional>
    );
  }, []);

  const steps: StepData[] = useMemo(() => {
    if (isZspaceInternalOrCustomerUser) {
      return [
        {
          title: HomeSections.SALES_ORDERS,
          onClick: navigateToMySalesOrdersPage,
        },
        { title: HomeSections.TEAM, onClick: navigateToMyTeamPage },
        {
          title: HomeSections.DEVICES,
          informationTag: (
            <InformationTag
              tasks={userTasks.devicesPendingTasks}
              isLoading={isLoadingDevicesHomeAlert}
            />
          ),
          onClick: navigateToDevicesPage,
        },
        {
          title: HomeSections.SOFTWARE,
          informationTag: (
            <InformationTag
              tasks={userTasks.softwarePendingTasks}
              isLoading={isLoadingSoftwareSeatsHomeAlert}
            />
          ),
          onClick: navigateToSoftwarePage,
        },
      ];
    }
    return [];
  }, [
    InformationTag,
    isLoadingDevicesHomeAlert,
    isLoadingSoftwareSeatsHomeAlert,
    isZspaceInternalOrCustomerUser,
    navigateToDevicesPage,
    navigateToMySalesOrdersPage,
    navigateToMyTeamPage,
    navigateToSoftwarePage,
    userTasks.devicesPendingTasks,
    userTasks.softwarePendingTasks,
  ]);

  const isActiveStepTab = useCallback(
    (tab: HomeSections) => {
      return tab === activeTab;
    },
    [activeTab]
  );

  const getDevicesHomeAlertData = useCallback(
    async () =>
      executeFetchDevicesHomeAlertRequest({
        asyncFunction: async () => {
          const response = await fetchDevicesHomeAlertData();
          setDevicesTasksData(response);
        },
      }),
    [executeFetchDevicesHomeAlertRequest]
  );

  const getSoftwareSeatsHomeAlertDataRequest = useCallback(
    async () =>
      executeFetchSoftwareSeatsHomeAlertRequest({
        asyncFunction: async () => {
          const response = await fetchSoftwareSeatsHomeAlertData();
          setSoftwareTasksData(response);
        },
      }),
    [executeFetchSoftwareSeatsHomeAlertRequest]
  );

  const getAllAlerts = useCallback(async () => {
    await Promise.all([
      getDevicesHomeAlertData(),
      getSoftwareSeatsHomeAlertDataRequest(),
    ]);
  }, [getDevicesHomeAlertData, getSoftwareSeatsHomeAlertDataRequest]);

  useEffect(() => {
    if (isZspaceInternalOrCustomerUser) {
      getAllAlerts();
    }
  }, [
    getAllAlerts,
    isCustomerUser,
    isZspaceInternalOrCustomerUser,
    isZspaceInternalUser,
  ]);

  useEffect(() => {
    switch (refreshAction?.action) {
      case HomeSectionsRefreshAction.ALL:
        getAllAlerts();
        break;
      case HomeSectionsRefreshAction.DEVICES:
        getDevicesHomeAlertData();
        break;
      case HomeSectionsRefreshAction.SOFTWARE:
        getSoftwareSeatsHomeAlertDataRequest();
        break;
    }
  }, [
    getAllAlerts,
    getDevicesHomeAlertData,
    getSoftwareSeatsHomeAlertDataRequest,
    refreshAction,
  ]);

  return (
    <Element className={`${styles.container} mt-4 mx-10`}>
      <Columns
        display="flex"
        alignItems="stretch"
        className={`is-gapless ${styles.stepColumns}`}
      >
        {steps.map((step) => (
          <Columns.Column key={step.title} display="flex">
            <StepTab
              title={step.title}
              informationTag={step.informationTag}
              active={isActiveStepTab(step.title)}
              onClick={step.onClick}
            />
          </Columns.Column>
        ))}
      </Columns>
    </Element>
  );
}

export default HomeSectionsHeaderTabs;
